import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, MenuItem,
  Alert, Typography, Box, CircularProgress, InputAdornment
} from '@mui/material';
import { UploadFile as UploadFileIcon } from '@mui/icons-material';
import WalletService from '../../services/wallet.service';
import { MAX_FILE_BYTES, MAX_FILE_LABEL } from '../../utils/fileSizeLimit';

const ACCEPTED_TYPES = ['application/pdf'];
const isAcceptedType = (file) => file.type.startsWith('image/') || ACCEPTED_TYPES.includes(file.type);
const TOPUP_ACCOUNT_TYPES = ['OPERATIONS', 'CASH'];

/**
 * TC-entered top-up on a customer's wallet. Same request as a portal top-up (bank account, amount,
 * remarks, proof) — it lands as PENDING and is approved or rejected on the Wallets → Top-ups list.
 * Unlike the portal, the money can also be cash received at the counter (a CASH account); then the proof is optional.
 */
const WalletTopUpDialog = ({ open, walletId, organizationName, onClose, onSubmitted }) => {
  const [bankAccounts, setBankAccounts] = useState([]);
  const [banksLoading, setBanksLoading] = useState(false);
  const [bankId, setBankId] = useState('');
  const [amount, setAmount] = useState('');
  const [remarks, setRemarks] = useState('');
  const [bankReference, setBankReference] = useState('');
  const [file, setFile] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setBankId('');
    setAmount('');
    setRemarks('');
    setBankReference('');
    setFile(null);
    setError(null);
    setBanksLoading(true);
    // Active OPERATIONS accounts (bank transfer, as the portal offers) plus CASH accounts (cash at the counter).
    WalletService.getBankAccounts()
      .then((list) => {
        const usable = (list || []).filter((b) => b.isActive && TOPUP_ACCOUNT_TYPES.includes(b.accountType));
        setBankAccounts(usable);
        const preferred = usable.find((b) => b.isDefault) || usable[0];
        if (preferred) setBankId(preferred.id);
      })
      .catch(() => setError('Failed to load bank accounts'))
      .finally(() => setBanksLoading(false));
  }, [open]);

  const handleFileChange = (e) => {
    const picked = e.target.files?.[0];
    e.target.value = '';
    if (!picked) return;
    if (!isAcceptedType(picked)) {
      setError('Proof must be an image or a PDF');
      return;
    }
    if (picked.size > MAX_FILE_BYTES) {
      setError(`${picked.name} is larger than ${MAX_FILE_LABEL}`);
      return;
    }
    setError(null);
    setFile(picked);
  };

  const amountValue = parseFloat(amount);
  const isCash = bankAccounts.find((b) => b.id === bankId)?.accountType === 'CASH';
  const canSubmit = !!bankId && amountValue > 0 && (!!file || isCash) && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await WalletService.submitTopupForWallet(walletId, {
        tcBankAccountId: bankId,
        amount: amountValue,
        remarks: remarks.trim() || null,
        bankReference: bankReference.trim() || null
      }, file || null);
      onSubmitted && onSubmitted();
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.error || 'Failed to submit the top-up');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !submitting && onClose()} maxWidth="sm" fullWidth>
      <DialogTitle>Top Up Wallet</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Record a top-up for <strong>{organizationName}</strong>. It is added as pending and credited once
          it is approved on the Wallets → Top-ups list.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {!banksLoading && bankAccounts.length === 0 && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No active operations or cash account is set up. Add one under Wallets → Bank Accounts first.
          </Alert>
        )}

        <TextField
          select
          fullWidth
          size="small"
          label="Received into"
          value={bankId}
          onChange={(e) => setBankId(e.target.value)}
          disabled={banksLoading || bankAccounts.length === 0}
          sx={{ mb: 2 }}
        >
          {bankAccounts.map((b) => (
            <MenuItem key={b.id} value={b.id}>
              {b.accountType === 'CASH'
                ? `Cash - ${b.accountName}`
                : `${b.bankName} - ${b.accountName}${b.accountNumber ? ` (${b.accountNumber})` : ''}`}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          fullWidth
          size="small"
          type="number"
          label="Amount"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          inputProps={{ min: 0, step: '0.01' }}
          InputProps={{ startAdornment: <InputAdornment position="start">AED</InputAdornment> }}
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth
          size="small"
          label="Bank reference (optional)"
          value={bankReference}
          onChange={(e) => setBankReference(e.target.value)}
          inputProps={{ maxLength: 100 }}
          helperText="The bank's reference for the transfer; it shows on the customer's statement"
          sx={{ mb: 2 }}
        />

        <TextField
          fullWidth
          size="small"
          label="Remarks (optional)"
          value={remarks}
          onChange={(e) => setRemarks(e.target.value)}
          multiline
          rows={2}
          inputProps={{ maxLength: 500 }}
          sx={{ mb: 2 }}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Button component="label" variant="outlined" startIcon={<UploadFileIcon />} disabled={submitting}>
            {file ? 'Change proof' : 'Upload proof'}
            <input hidden type="file" accept="image/*,.pdf" onChange={handleFileChange} />
          </Button>
          <Typography variant="body2" color={file ? 'text.primary' : 'text.secondary'}>
            {file ? file.name : `${isCash ? 'Optional' : 'Required'} — image or PDF, up to ${MAX_FILE_LABEL}`}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit}
          startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : null}
        >
          {submitting ? 'Submitting…' : 'Submit Top-up'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WalletTopUpDialog;
